"use client";

import React from "react";
import { motion } from "framer-motion";
import { IoDocumentText } from "react-icons/io5";
import {
  staggerContainer,
  staggerItem,
  fadeInUp,
  textReveal,
} from "../lib/animations";

interface ModalLink {
  text: string;
  url: string;
}

interface ModalBullet {
  text: string;
  links?: ModalLink[];
}

interface ModalParagraph {
  text?: string;
  isBold?: boolean;
  bullets?: ModalBullet[];
  links?: ModalLink[];
  buttons?: ModalButton[];
}

interface ModalButton {
  text: string;
  action?: () => void;
  variant?: "primary" | "secondary";
}

interface ModalSection {
  paragraphs: ModalParagraph[];
  images?: string[];
  layout?: "text-only" | "text-image" | "image-text" | "text-top" | "image-top";
  title?: string;
}

const renderLinks = (links: ModalLink[]) => (
  <div className="flex flex-wrap gap-2 mt-2">
    {links.map((link, idx) => (
      <a
        key={idx}
        href={link.url}
        className="text-blue-600 hover:text-blue-800 underline lg:text-xl text-md"
        target="_blank"
        rel="noopener noreferrer"
      >
        {link.text}
      </a>
    ))}
  </div>
);

const renderBullets = (bullets: ModalBullet[]) => (
  <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
    {bullets.map((bullet, idx) => (
      <li key={idx} className="text-gray-600 lg:text-xl text-md">
        {bullet.text}
        {bullet.links && renderLinks(bullet.links)}
      </li>
    ))}
  </ul>
);

const renderParagraph = (paragraph: ModalParagraph, idx: number) => (
  <motion.div
    key={idx}
    className="mb-4"
    initial={{ opacity: 0, y: 15 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: idx * 0.1 }}
  >
    <p
      className={`text-gray-600 lg:text-xl text-xl leading-relaxed ${
        paragraph.isBold ? "font-semibold" : ""
      }`}
    >
      {paragraph.text}
      {paragraph.links && renderLinks(paragraph.links)}
    </p>
    {paragraph.bullets && renderBullets(paragraph.bullets)}
  </motion.div>
);

const renderSection = (section: ModalSection, sectionIdx: number) => (
  <div key={sectionIdx} className="mb-8">
    <div
      className={`${
        section.layout === "text-image"
          ? "grid grid-cols-1 lg:grid-cols-1 lg:gap-6 gap-2"
          : section.layout === "image-text"
          ? "grid grid-cols-1 lg:grid-cols-2 lg:gap-6 gap-2"
          : section.layout === "text-top"
          ? "flex flex-col lg:gap-6 gap-2"
          : section.layout === "image-top"
          ? "flex flex-col lg:gap-6 gap-2"
          : "block"
      }`}
    >
      {/* Text Content */}
      <div
        className={`w-full bg-[#f5f5f7] rounded-3xl ${
          section?.layout === "text-only" || !section?.layout
            ? " p-5"
            : "pb-0 lg:pt-10 pt-5 p-10"
        }`}
      >
        {section?.title && (
          <motion.h3
            className="lg:text-3xl text-lg font-bold text-gray-800 mb-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            {section?.title}
          </motion.h3>
        )}
        <div
          className={`${
            section?.images && section?.images?.length === 0 ? "pb-0" : ""
          } ${
            section.layout === "image-text"
              ? "order-2"
              : section.layout === "image-top"
              ? "order-2"
              : "lg:mb-10 mb-3 "
          }`}
        >
          {section.paragraphs.map((paragraph, idx) =>
            renderParagraph(paragraph, idx)
          )}
        </div>
      </div>
    </div>
  </div>
);

const termsData: {
  category: string;
  title: string;
  subtitle: string;
  sections: ModalSection[];
} = {
  category: "Legal & Compliance",
  title: "Terms of Use",
  subtitle: "Terms and conditions governing your use of our services",
  sections: [
    {
      title: "1. Acceptance of Terms",
      paragraphs: [
        {
          text: "By accessing and using ZySoftec's website and services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.",
        },
      ],
      layout: "text-only",
    },
    {
      title: "2. Services Description",
      paragraphs: [
        {
          text: "ZySoftec provides software development services including but not limited to:",
          bullets: [
            { text: "Custom web and mobile application development" },
            { text: "UI/UX design and product prototyping" },
            { text: "AI and machine learning integration" },
            { text: "Enterprise CRM/ERP solutions" },
            { text: "Cloud infrastructure and DevOps services" },
            { text: "Consulting and technical support" },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "3. User Responsibilities",
      paragraphs: [
        {
          text: "As a user of our services, you agree to:",
          bullets: [
            { text: "Provide accurate and complete information" },
            { text: "Maintain the security of your account credentials" },
            { text: "Use our services only for lawful purposes" },
            { text: "Not interfere with or disrupt our services" },
            { text: "Respect intellectual property rights" },
            { text: "Comply with all applicable laws and regulations" },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "4. Intellectual Property Rights",
      paragraphs: [
        {
          text: "All content, features, and functionality on our website, including but not limited to text, graphics, logos, and software, are owned by ZySoftec and are protected by copyright, trademark, and other intellectual property laws.",
        },
        {
          text: "For custom development projects, intellectual property rights are typically transferred to the client upon full payment, unless otherwise specified in the project agreement.",
        },
      ],
      layout: "text-only",
    },
    {
      title: "5. Payment Terms",
      paragraphs: [
        {
          text: "Payment terms for our services are as follows:",
          bullets: [
            {
              text: "Payment schedules are outlined in individual project agreements",
            },
            {
              text: "All payments are due in the currency specified in the agreement",
            },
            {
              text: "Late payments may result in project delays or suspension",
            },
            { text: "Refunds are subject to our refund policy" },
            { text: "All fees are exclusive of applicable taxes" },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "6. Project Terms and Conditions",
      paragraphs: [
        {
          text: "For custom development projects:",
          bullets: [
            {
              text: "Project scope and deliverables are defined in written agreements",
            },
            { text: "Timelines are estimates and may be subject to change" },
            {
              text: "Client feedback and approval are required at designated milestones",
            },
            {
              text: "Changes to project scope may affect timeline and pricing",
            },
            {
              text: "Final deliverables are provided upon project completion and payment",
            },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "7. Confidentiality and Non-Disclosure",
      paragraphs: [
        {
          text: "We are committed to maintaining the confidentiality of your business information and trade secrets. We will not disclose confidential information to third parties without your written consent, except as required by law.",
        },
      ],
      layout: "text-only",
    },
    {
      title: "8. Limitation of Liability",
      paragraphs: [
        {
          text: "ZySoftec shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or use, arising out of or relating to our services.",
        },
        {
          text: "Our total liability for any claims arising from the use of our services shall not exceed the amount paid by you for the specific service in question.",
        },
      ],
      layout: "text-only",
    },
    {
      title: "9. Warranty Disclaimer",
      paragraphs: [
        {
          text: "Our services are provided 'as is' and 'as available' without warranties of any kind, either express or implied. We do not warrant that our services will be uninterrupted, error-free, or secure.",
        },
      ],
      layout: "text-only",
    },
    {
      title: "10. Termination",
      paragraphs: [
        {
          text: "Either party may terminate this agreement with written notice. Upon termination:",
          bullets: [
            { text: "You will cease using our services" },
            { text: "We will stop providing services to you" },
            { text: "Outstanding payments remain due" },
            { text: "Confidentiality obligations survive termination" },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "11. Governing Law and Jurisdiction",
      paragraphs: [
        {
          text: "These terms shall be governed by and construed in accordance with the laws of Pakistan. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in Pakistan.",
        },
      ],
      layout: "text-only",
    },
    {
      title: "12. Severability",
      paragraphs: [
        {
          text: "If any provision of these terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these terms will otherwise remain in full force and effect.",
        },
      ],
      layout: "text-only",
    },
    {
      title: "13. Changes to Terms",
      paragraphs: [
        {
          text: "We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting on our website. Your continued use of our services after changes are posted constitutes acceptance of the modified terms.",
        },
      ],
      layout: "text-only",
    },
    {
      title: "14. Contact Information",
      paragraphs: [
        {
          text: "If you have any questions about these Terms of Use, please contact us:",
        },
        {
          text: "Email: hello@zysoftec.com",
          isBold: true,
        },
        {
          text: "Phone: +92 3175682252",
          isBold: true,
        },
        {
          text: "Address: ZySoftec, Pakistan",
          isBold: true,
        },
      ],
      layout: "text-only",
    },
  ],
};

export default function TermsOfUse() {
  return (
    <motion.div
      className="min-h-screen bg-white"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      {/* Header */}
      <motion.div
        className="lg:pt-20 pt-5 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {termsData.category && (
          <motion.p
            className="lg:text-base text-sm text-gray-500 font-medium mb-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            {termsData.category}
          </motion.p>
        )}
        <motion.h2
          className="lg:text-4xl text-2xl font-bold text-gray-900 leading-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          {termsData.title}
        </motion.h2>

        {termsData.subtitle && (
          <motion.p
            className="lg:text-lg text-sm text-gray-600 mt-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            {termsData.subtitle}
          </motion.p>
        )}
      </motion.div>

      {/* Main Content */}
      <div className="lg:pt-10 pt-5 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-5">
        {termsData.sections.map((section, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 + idx * 0.1 }}
          >
            {renderSection(section, idx)}
          </motion.div>
        ))}
      </div>

      {/* Last Updated */}
      <motion.div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 1 }}
      >
        <div className="bg-gray-50 rounded-lg p-4 text-center max-w-4xl mx-auto">
          <p className="text-sm text-gray-600">
            <strong>Last Updated:</strong> December 2024
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
